/**
 * FinishingSoonModal - Modal para notificar al siguiente cliente
 * QUEUE-006: Barbero indica que está acabando con cliente actual
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Bell, User } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { QueueTicket } from '../../types';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';

interface FinishingSoonModalProps {
  currentTicket: QueueTicket;
  nextTicket?: QueueTicket;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function FinishingSoonModal({
  currentTicket,
  nextTicket,
  isOpen,
  onClose,
  onSuccess,
}: FinishingSoonModalProps) {
  const { t } = useTranslation();
  const [selectedMinutes, setSelectedMinutes] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const FINISH_TIME_OPTIONS = [
    { value: 5, label: t('queue.finishingIn5'), description: t('queue.almostDone') },
    { value: 10, label: t('queue.finishingIn10'), description: t('queue.wrappingUp') },
    { value: 15, label: t('queue.finishingIn15'), description: t('queue.moreTime') },
  ];

  const handleNotify = async () => {
    setIsSubmitting(true);

    try {
      const notifyFinishingSoon = httpsCallable(functions, 'notifyFinishingSoon');
      const result = await notifyFinishingSoon({
        queueId: currentTicket.queueId,
        minutesRemaining: selectedMinutes,
      });

      const data = result.data as { success: boolean; message: string };

      if (data.success) {
        setShowConfirmation(true);
        toast.success(`✅ Siguiente cliente notificado - ${selectedMinutes} min`, { duration: 4000 });

        setTimeout(() => {
          setShowConfirmation(false);
          onSuccess?.();
          handleClose();
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error notifying finishing soon:', error);
      toast.error(error.message || t('errors.notificationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedMinutes(5);
      setShowConfirmation(false);
      onClose();
    }
  };

  const getPreviewMessage = () => {
    return t('queue.finishingSoonMessage', { minutes: selectedMinutes });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative bg-surface border border-hairline rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-hairline">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-surface-2 rounded-lg">
                <Bell className="w-6 h-6 text-ink-subtle" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-sans text-lg font-semibold text-ink">{t('queue.finishingSoon')}</h3>
                <p className="text-sm text-ink-muted">{t('queue.notifyNextClient')}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 text-ink-subtle hover:text-ink hover:bg-surface-2 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Success Animation */}
        {showConfirmation && (
          <div className="absolute inset-0 bg-surface flex items-center justify-center z-10 rounded-2xl">
            <div className="text-center">
              <Bell className="w-12 h-12 text-accent mx-auto mb-4" aria-hidden="true" />
              <p className="text-xl font-semibold text-ink">{t('queue.clientNotified')}</p>
              <p className="text-sm text-ink-muted mt-2">{t('queue.preparing')}</p>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current & Next Ticket Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-surface-2 border border-hairline rounded-lg">
              <div className="flex items-center gap-2 text-ink-subtle mb-2">
                <User className="w-4 h-4" aria-hidden="true" />
                <span className="text-xs font-medium uppercase tracking-wide">{t('queue.currentClient')}</span>
              </div>
              <p className="font-semibold text-ink tabular-nums">#{currentTicket.ticketNumber}</p>
              <p className="text-xs text-ink-muted mt-1">{t('queue.inService')}</p>
            </div>

            <div className="p-4 bg-surface-2 border border-accent/30 rounded-lg">
              <div className="flex items-center gap-2 text-accent mb-2">
                <Bell className="w-4 h-4" aria-hidden="true" />
                <span className="text-xs font-medium uppercase tracking-wide">{t('common.next')}</span>
              </div>
              {nextTicket ? (
                <>
                  <p className="font-semibold text-ink tabular-nums">#{nextTicket.ticketNumber}</p>
                  <p className="text-xs text-ink-muted mt-1">{t('queue.willBeNotified')}</p>
                </>
              ) : (
                <p className="text-sm text-ink-subtle">{t('queue.noNextClient')}</p>
              )}
            </div>
          </div>

          {/* Time Selection */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-3">
              {t('queue.howLongToFinish')}
            </label>
            <div className="space-y-2">
              {FINISH_TIME_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedMinutes(option.value)}
                  disabled={isSubmitting}
                  className={`
                    w-full p-4 rounded-lg border transition-colors flex items-center gap-4
                    ${
                      selectedMinutes === option.value
                        ? 'border-accent bg-accent/10'
                        : 'border-hairline bg-surface-2 hover:border-hairline-strong'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  <div className="text-2xl font-semibold text-ink tabular-nums w-8">{option.value}</div>
                  <div className="flex-1 text-left">
                    <div className="font-semibold text-ink">{option.label}</div>
                    <div className="text-xs text-ink-muted">{option.description}</div>
                  </div>
                  {selectedMinutes === option.value && (
                    <div className="w-5 h-5 bg-accent rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-on-accent rounded-full" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Message Preview */}
          <div className="p-4 bg-surface-2 rounded-lg border border-hairline">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-surface rounded-lg mt-0.5">
                <Bell className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-ink-subtle uppercase tracking-wide mb-1">
                  {t('common.messagePreview')}
                </p>
                <p className="text-sm text-ink-muted leading-relaxed">
                  "{getPreviewMessage()}"
                </p>
              </div>
            </div>
          </div>

          {/* Warning if no next client */}
          {!nextTicket && (
            <div className="p-3 bg-warning/10 border border-warning/30 rounded-lg">
              <p className="text-sm text-ink">
                {t('queue.noNextWarning')}
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 bg-surface-2 border-t border-hairline flex gap-3">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 text-ink bg-surface border border-hairline rounded-lg font-medium hover:bg-surface-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleNotify}
            disabled={!nextTicket || isSubmitting}
            className="flex-1 px-4 py-3 bg-accent text-on-accent rounded-lg font-medium hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-on-accent border-t-transparent rounded-full animate-spin" />
                {t('queue.notifying')}
              </span>
            ) : (
              t('queue.notifyClient')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
